import { forwardRef, type InputHTMLAttributes, type ReactNode, type TextareaHTMLAttributes } from 'react';

import { cn } from '../cn';

const fieldBase =
  'w-full min-w-0 rounded-sm border border-border-strong bg-surface text-body text-ink transition-[border-color,box-shadow] duration-(--duration-fade) ' +
  'hover:border-ink-muted focus-visible:border-focus focus-visible:ring-2 focus-visible:ring-focus/20 ' +
  'aria-invalid:border-negative aria-invalid:focus-visible:ring-negative/15 ' +
  'disabled:cursor-not-allowed disabled:bg-surface-muted disabled:text-ink-muted ' +
  // :read-only also matches buttons and divs, so scope it to real text fields.
  '[&:is(input,textarea):read-only]:bg-surface-muted [&:is(input,textarea):read-only]:hover:border-border-strong';

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  /** Content inside the field at the inline start (e.g. "AED"). */
  startAdornment?: ReactNode;
  endAdornment?: ReactNode;
  /** sm 28 (toolbar) · md 32 (default) · lg 36 (sign-in). */
  size?: 'sm' | 'md' | 'lg';
}

/**
 * A text field. With an adornment, `dir` and `className` apply to the wrapper, not the inner input: the
 * adornment is positioned and the input's padding reserved in the same direction, so a technical value
 * (`dir="ltr"`, e.g. "AED 250,000.00") never overlaps its adornment in an RTL page. Padding is logical only.
 */
export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { className, startAdornment, endAdornment, size = 'md', dir, ...props },
  ref,
) {
  const height = size === 'sm' ? 'h-7' : size === 'lg' ? 'h-9' : 'h-8';
  if (!startAdornment && !endAdornment) {
    return <input ref={ref} data-slot="input" dir={dir} className={cn(fieldBase, height, 'px-2.5', className)} {...props} />;
  }
  return (
    <div data-slot="input-group" dir={dir} className={cn('relative flex items-center', className)}>
      {startAdornment ? (
        <span
          data-slot="input-start"
          className="pointer-events-none absolute start-0 flex h-full items-center border-e border-border ps-2.5 pe-2 text-label text-ink-muted"
        >
          {startAdornment}
        </span>
      ) : null}
      <input
        ref={ref}
        data-slot="input"
        className={cn(fieldBase, height, startAdornment ? 'ps-14' : 'ps-2.5', endAdornment ? 'pe-9' : 'pe-2.5')}
        {...props}
      />
      {endAdornment ? (
        <span data-slot="input-end" className="absolute end-0 flex h-full items-center pe-2 text-ink-muted">
          {endAdornment}
        </span>
      ) : null}
    </div>
  );
});

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(function Textarea(
  { className, rows = 3, ...props },
  ref,
) {
  return <textarea ref={ref} rows={rows} className={cn(fieldBase, 'px-2.5 py-1.5 leading-5', className)} {...props} />;
});

export { fieldBase as fieldClassName };
